import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDownIcon } from "../components/Icons";
import {
	getCountryInfo,
	groupCountriesByRegion,
	sortRegions,
} from "./CountryCodeUtils";

const BurgerIcon = () => (
	<svg
		width="20"
		height="20"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="3" y1="6" x2="21" y2="6"></line>
		<line x1="3" y1="12" x2="21" y2="12"></line>
		<line x1="3" y1="18" x2="21" y2="18"></line>
	</svg>
);

const UploadIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="17,8 12,3 7,8"></polyline>
		<line x1="12" y1="3" x2="12" y2="15"></line>
	</svg>
);

const RemoveIcon = () => (
	<svg
		width="12"
		height="12"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<line x1="18" y1="6" x2="6" y2="18"></line>
		<line x1="6" y1="6" x2="18" y2="18"></line>
	</svg>
);

const SectionHeader = ({
	title,
	isExpanded,
	onToggle,
	hasActiveFilters = false,
	style = {},
}) => (
	<div
		className={`section-header ${isExpanded ? "expanded" : "collapsed"}`}
		onClick={onToggle}
		style={style}
	>
		<span className="section-title">{title}</span>
		<div className="section-indicators">
			<ChevronDownIcon />
		</div>
	</div>
);

const CategoryHeader = ({ title, isExpanded, onClick, children }) => (
	<div className="filter-category-box">
		<div
			className={`category-header ${isExpanded ? "expanded" : "collapsed"}`}
			onClick={onClick}
		>
			<h3 className="category-title-new">{title}</h3>
			<div
				className={`category-chevron ${isExpanded ? "expanded" : "collapsed"}`}
			>
				<ChevronDownIcon />
			</div>
		</div>
		{isExpanded && (
			<div
				className="category-content"
				style={{ paddingLeft: "8px", paddingRight: "8px" }}
			>
				{children}
			</div>
		)}
	</div>
);

const ThresholdInput = React.memo(
	({ field, label, hint, placeholder, value, defaultValue, onChange }) => {
		const [localValue, setLocalValue] = useState("");
		const [isFocused, setIsFocused] = useState(false);

		// Helper function to format large numbers for display
		const formatNumber = (num) => {
			if (num >= 1000000000000) {
				return (num / 1000000000000).toFixed(1) + "T";
			} else if (num >= 1000000000) {
				return (num / 1000000000).toFixed(1) + "B";
			} else if (num >= 1000000) {
				return (num / 1000000).toFixed(1) + "M";
			} else if (num >= 1000) {
				return (num / 1000).toFixed(1) + "K";
			}
			return num.toString();
		};

		// Helper function to parse input values
		const parseInputValue = (inputStr) => {
			if (!inputStr || inputStr === "") {
				return defaultValue;
			}

			const str = inputStr.toString().toLowerCase();
			let multiplier = 1;
			let baseValue = str;

			if (str.includes("t")) {
				multiplier = 1000000000000;
				baseValue = str.replace("t", "");
			} else if (str.includes("b")) {
				multiplier = 1000000000;
				baseValue = str.replace("b", "");
			} else if (str.includes("m")) {
				multiplier = 1000000;
				baseValue = str.replace("m", "");
			} else if (str.includes("k")) {
				multiplier = 1000;
				baseValue = str.replace("k", "");
			}

			const parsed = parseFloat(baseValue);
			return isNaN(parsed) ? defaultValue : parsed * multiplier;
		};

		// Update local value when prop value changes (but not when focused)
		useEffect(() => {
			if (!isFocused) {
				setLocalValue(formatNumber(value || defaultValue));
			}
		}, [value, defaultValue, isFocused]);

		const handleChange = useCallback((e) => {
			setLocalValue(e.target.value);
		}, []);

		const handleFocus = useCallback(
			(e) => {
				setIsFocused(true);
				// Show raw number when focused
				setLocalValue((value || defaultValue).toString());
			},
			[value, defaultValue],
		);

		const handleBlur = useCallback(() => {
			setIsFocused(false);
			const parsedValue = parseInputValue(localValue);

			// Update parent if value changed
			if (parsedValue !== value) {
				onChange(field, parsedValue);
			}

			// Format the display value
			setLocalValue(formatNumber(parsedValue));
		}, [localValue, value, field, onChange]);

		const handleKeyPress = useCallback((e) => {
			if (e.key === "Enter") {
				e.target.blur(); // This will trigger handleBlur
			}
		}, []);

		return (
			<div className="threshold-input-group">
				<label className="threshold-input-label">
					{label}
					<span className="threshold-hint">({hint})</span>
				</label>
				<div className="threshold-input-wrapper">
					<input
						type="text"
						placeholder={placeholder}
						className="threshold-input"
						value={localValue}
						onChange={handleChange}
						onFocus={handleFocus}
						onBlur={handleBlur}
						onKeyPress={handleKeyPress}
					/>
					<span className="threshold-display">
						Current: {formatNumber(value || defaultValue)}
					</span>
				</div>
			</div>
		);
	},
);

// Country MultiSelect Component with proper display names
const CountryMultiSelectDropdown = ({
	label,
	options = [],
	value = [],
	onChange,
	placeholder = "Select countries...",
	showLimit = 50,
	searchPlaceholder = "Search countries...",
	maxHeight = "300px",
	className = "",
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
	});
	const [pendingSelections, setPendingSelections] = useState(value);
	const [showAll, setShowAll] = useState(false);

	const dropdownRef = useRef(null);
	const triggerRef = useRef(null);

	// Update pendingSelections when value prop changes
	useEffect(() => {
		setPendingSelections(value);
	}, [value]);

	// Enhanced positioning function that accounts for scroll
	const updateDropdownPosition = useCallback(() => {
		if (triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX,
				width: rect.width,
			});
		}
	}, []);

	// Add scroll event listener to update position
	useEffect(() => {
		if (isOpen) {
			updateDropdownPosition();

			const handleScroll = () => {
				updateDropdownPosition();
			};

			window.addEventListener("scroll", handleScroll, true);
			document.addEventListener("scroll", handleScroll, true);
			window.addEventListener("resize", handleScroll);

			return () => {
				window.removeEventListener("scroll", handleScroll, true);
				document.removeEventListener("scroll", handleScroll, true);
				window.removeEventListener("resize", handleScroll);
			};
		}
	}, [isOpen, updateDropdownPosition]);

	// Click outside handler
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target) &&
				triggerRef.current &&
				!triggerRef.current.contains(event.target)
			) {
				setIsOpen(false);
				setSearchTerm("");
				setPendingSelections(value);
				setShowAll(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [value]);

	// Create country options with display names and group by region
	const countryOptionsWithNames = options.map((countryCode) => {
		const countryInfo = getCountryInfo(countryCode);
		return {
			code: countryCode,
			name: countryInfo.name,
			displayText: `${countryInfo.name} (${countryCode})`,
			region: countryInfo.region,
			isSpecial: countryInfo.isSpecialCode,
		};
	});

	// Filter options based on search term
	const filteredOptions = countryOptionsWithNames.filter(
		(country) =>
			country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			country.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
			country.region.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	// Group countries by region
	const groupedOptions = groupCountriesByRegion(
		filteredOptions.map((c) => c.code),
	);
	const sortedRegions = sortRegions(Object.keys(groupedOptions));

	// Determine which options to show (limited or all)
	const displayOptions = showAll
		? filteredOptions
		: filteredOptions.slice(0, showLimit);

	const toggleOption = (countryCode) => {
		setPendingSelections((prev) => {
			if (prev.includes(countryCode)) {
				return prev.filter((v) => v !== countryCode);
			} else {
				return [...prev, countryCode];
			}
		});
	};

	const handleDone = () => {
		onChange(pendingSelections);
		setIsOpen(false);
		setSearchTerm("");
		setShowAll(false);
	};

	const clearAll = () => {
		setPendingSelections([]);
	};

	const selectAll = () => {
		setPendingSelections([...filteredOptions.map((c) => c.code)]);
	};

	const handleTriggerClick = () => {
		if (!isOpen) {
			setPendingSelections(value);
		}
		setIsOpen(!isOpen);
	};

	const handleShowMore = () => {
		setShowAll(true);
	};

	const handleShowLess = () => {
		setShowAll(false);
	};

	return (
		<div className={`multiselect-container ${className}`}>
			{label && (
				<label className="multiselect-label">
					{label}
					{value.length > 0 && (
						<span className="selected-count">({value.length} selected)</span>
					)}
				</label>
			)}

			<div
				ref={triggerRef}
				className={`multiselect-trigger ${isOpen ? "open" : ""}`}
				onClick={handleTriggerClick}
			>
				<span className="multiselect-placeholder">
					{value.length === 0
						? placeholder
						: `${value.length} countr${value.length !== 1 ? "ies" : "y"} selected`}
				</span>
				<ChevronDownIcon />
			</div>

			{isOpen &&
				createPortal(
					<div
						ref={dropdownRef}
						className="multiselect-dropdown-portal"
						style={{
							position: "absolute",
							top: dropdownPosition.top,
							left: dropdownPosition.left,
							width: dropdownPosition.width,
							zIndex: 10000,
							pointerEvents: "auto",
						}}
					>
						<div className="multiselect-header">
							<input
								type="text"
								placeholder={searchPlaceholder}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="multiselect-search"
								onClick={(e) => e.stopPropagation()}
							/>
							<div className="multiselect-actions">
								<button
									type="button"
									onClick={selectAll}
									className="select-all-btn"
								>
									Select All
								</button>
								<button
									type="button"
									onClick={clearAll}
									className="clear-all-btn"
								>
									Clear
								</button>
							</div>
						</div>

						<div className="multiselect-options" style={{ maxHeight }}>
							{searchTerm
								? // Show flat list when searching
									displayOptions.map((country, index) => (
										<div
											key={`${country.code}-${index}`}
											className={`multiselect-option ${pendingSelections.includes(country.code) ? "selected" : ""}`}
											onClick={() => toggleOption(country.code)}
										>
											<input
												type="checkbox"
												checked={pendingSelections.includes(country.code)}
												onChange={() => toggleOption(country.code)}
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
												}}
											/>
											<span className="option-text">{country.displayText}</span>
										</div>
									))
								: // Show grouped list when not searching
									sortedRegions.map((region) => {
										const regionCountries = groupedOptions[region]
											.map((code) =>
												countryOptionsWithNames.find((c) => c.code === code),
											)
											.filter(Boolean)
											.slice(0, showAll ? undefined : showLimit);

										return (
											<div key={region}>
												<div className="region-header">{region}</div>
												{regionCountries.map((country, index) => (
													<div
														key={`${country.code}-${index}`}
														className={`multiselect-option region-option ${pendingSelections.includes(country.code) ? "selected" : ""}`}
														onClick={() => toggleOption(country.code)}
													>
														<input
															type="checkbox"
															checked={pendingSelections.includes(country.code)}
															onChange={() => toggleOption(country.code)}
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
															}}
														/>
														<span className="option-text">
															{country.displayText}
														</span>
													</div>
												))}
											</div>
										);
									})}

							{/* Show More/Less buttons */}
							{filteredOptions.length > showLimit && (
								<div className="multiselect-show-controls">
									{!showAll ? (
										<button
											type="button"
											onClick={handleShowMore}
											className="show-more-btn"
										>
											Show More ({filteredOptions.length - showLimit} more)
										</button>
									) : (
										<button
											type="button"
											onClick={handleShowLess}
											className="show-less-btn"
										>
											Show Less
										</button>
									)}
								</div>
							)}

							{filteredOptions.length === 0 && (
								<div className="no-options">No countries found</div>
							)}
						</div>

						<div className="multiselect-footer">
							<div className="multiselect-help-text">
								Press "Done" to save selections
							</div>

							<button
								type="button"
								onClick={handleDone}
								className="done-button"
							>
								Done
							</button>
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
};

// Internal MultiSelectDropdown Component
const MultiSelectDropdown = ({
	label,
	options = [],
	value = [],
	onChange,
	placeholder = "Select options...",
	showLimit = 50,
	searchPlaceholder = "Search...",
	maxHeight = "300px",
	className = "",
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [dropdownPosition, setDropdownPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
	});
	const [pendingSelections, setPendingSelections] = useState(value);
	const [showAll, setShowAll] = useState(false);

	const dropdownRef = useRef(null);
	const triggerRef = useRef(null);

	// Update pendingSelections when value prop changes
	useEffect(() => {
		setPendingSelections(value);
	}, [value]);

	// Enhanced positioning function that accounts for scroll
	const updateDropdownPosition = useCallback(() => {
		if (triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + window.scrollY,
				left: rect.left + window.scrollX,
				width: rect.width,
			});
		}
	}, []);

	// Add scroll event listener to update position
	useEffect(() => {
		if (isOpen) {
			updateDropdownPosition();

			const handleScroll = () => {
				updateDropdownPosition();
			};

			window.addEventListener("scroll", handleScroll, true);
			document.addEventListener("scroll", handleScroll, true);
			window.addEventListener("resize", handleScroll);

			return () => {
				window.removeEventListener("scroll", handleScroll, true);
				document.removeEventListener("scroll", handleScroll, true);
				window.removeEventListener("resize", handleScroll);
			};
		}
	}, [isOpen, updateDropdownPosition]);

	// Click outside handler
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target) &&
				triggerRef.current &&
				!triggerRef.current.contains(event.target)
			) {
				setIsOpen(false);
				setSearchTerm("");
				setPendingSelections(value);
				setShowAll(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [value]);

	// Filter options based on search term
	const filteredOptions = options.filter((option) =>
		option.toString().toLowerCase().includes(searchTerm.toLowerCase()),
	);

	// Determine which options to show (limited or all)
	const displayOptions = showAll
		? filteredOptions
		: filteredOptions.slice(0, showLimit);

	const toggleOption = (option) => {
		setPendingSelections((prev) => {
			if (prev.includes(option)) {
				return prev.filter((v) => v !== option);
			} else {
				return [...prev, option];
			}
		});
	};

	const handleDone = () => {
		onChange(pendingSelections);
		setIsOpen(false);
		setSearchTerm("");
		setShowAll(false);
	};

	const clearAll = () => {
		setPendingSelections([]);
	};

	const selectAll = () => {
		setPendingSelections([...filteredOptions]);
	};

	const handleTriggerClick = () => {
		if (!isOpen) {
			setPendingSelections(value);
		}
		setIsOpen(!isOpen);
	};

	const handleShowMore = () => {
		setShowAll(true);
	};

	const handleShowLess = () => {
		setShowAll(false);
	};

	return (
		<div className={`multiselect-container ${className}`}>
			{label && (
				<label className="multiselect-label">
					{label}
					{value.length > 0 && (
						<span className="selected-count">({value.length} selected)</span>
					)}
				</label>
			)}

			<div
				ref={triggerRef}
				className={`multiselect-trigger ${isOpen ? "open" : ""}`}
				onClick={handleTriggerClick}
			>
				<span className="multiselect-placeholder">
					{value.length === 0
						? placeholder
						: `${value.length} item${value.length !== 1 ? "s" : ""} selected`}
				</span>
				<ChevronDownIcon />
			</div>

			{isOpen &&
				createPortal(
					<div
						ref={dropdownRef}
						className="multiselect-dropdown-portal"
						style={{
							position: "absolute",
							top: dropdownPosition.top,
							left: dropdownPosition.left,
							width: dropdownPosition.width,
							zIndex: 10000,
							pointerEvents: "auto",
						}}
					>
						<div className="multiselect-header">
							<input
								type="text"
								placeholder={searchPlaceholder}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="multiselect-search"
								onClick={(e) => e.stopPropagation()}
							/>
							<div className="multiselect-actions">
								<button
									type="button"
									onClick={selectAll}
									className="select-all-btn"
								>
									Select All
								</button>
								<button
									type="button"
									onClick={clearAll}
									className="clear-all-btn"
								>
									Clear
								</button>
							</div>
						</div>

						<div className="multiselect-options" style={{ maxHeight }}>
							{displayOptions.map((option, index) => (
								<div
									key={`${option}-${index}`}
									className={`multiselect-option ${pendingSelections.includes(option) ? "selected" : ""}`}
									onClick={() => toggleOption(option)}
								>
									<input
										type="checkbox"
										checked={pendingSelections.includes(option)}
										onChange={() => toggleOption(option)}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
										}}
									/>
									<span className="option-text">{option}</span>
								</div>
							))}

							{/* Show More/Less buttons */}
							{filteredOptions.length > showLimit && (
								<div className="multiselect-show-controls">
									{!showAll ? (
										<button
											type="button"
											onClick={handleShowMore}
											className="show-more-btn"
										>
											Show More ({filteredOptions.length - showLimit} more)
										</button>
									) : (
										<button
											type="button"
											onClick={handleShowLess}
											className="show-less-btn"
										>
											Show Less
										</button>
									)}
								</div>
							)}

							{filteredOptions.length === 0 && (
								<div className="no-options">No options found</div>
							)}
						</div>

						<div className="multiselect-footer">
							<div className="multiselect-help-text">
								Press "Done" to save selections
							</div>

							<button
								type="button"
								onClick={handleDone}
								className="done-button"
							>
								Done
							</button>
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
};

export const PackageFiltersSidebar = ({
	showFilters,
	filters,
	expandedSections,
	filterOptions,
	isLoadingFilters,
	filterError,
	onToggleSection,
	onUpdateFilter,
	onToggleArrayFilter,
	onClearAllFilters,
	onClose,
	loadFilterOptions,
}) => {
	const filtersContentRef = useRef(null);

	// State for collapsible categories
	const [expandedCategories, setExpandedCategories] = useState({
		packageDetails: false,
		targeting: false,
		dealInfo: false,
		audienceInfo: false,
		scale: false,
	});

	// Upload states
	const [uploadStates, setUploadStates] = useState({
		includeList: {
			isUploading: false,
			fileName: null,
			packageIds: [],
			error: null,
			isUploaded: false,
		},
		blockedList: {
			isUploading: false,
			fileName: null,
			packageIds: [],
			error: null,
			isUploaded: false,
		},
	});

	const [showUploadTooltip, setShowUploadTooltip] = useState(false);

	const toggleCategory = (category) => {
		setExpandedCategories((prev) => ({
			...prev,
			[category]: !prev[category],
		}));
	};

	const handleUpdateFilter = useCallback(
		(filterKey, value) => {
			if (onUpdateFilter) {
				onUpdateFilter(filterKey, value);
			}
		},
		[onUpdateFilter],
	);

	const handleToggleArrayFilter = useCallback(
		(filterKey, value) => {
			if (onToggleArrayFilter) {
				onToggleArrayFilter(filterKey, value);
			}
		},
		[onToggleArrayFilter],
	);

	// Mock file upload handlers
	const handleFileUpload = async (file, listType) => {
		if (!file) return;

		setUploadStates((prev) => ({
			...prev,
			[listType]: {
				...prev[listType],
				isUploading: true,
				error: null,
			},
		}));

		// Simulate upload process
		setTimeout(() => {
			setUploadStates((prev) => ({
				...prev,
				[listType]: {
					isUploading: false,
					fileName: file.name,
					packageIds: ["mock-package-1", "mock-package-2"],
					error: null,
					isUploaded: true,
				},
			}));
		}, 1500);
	};

	const clearUploadedList = (listType) => {
		setUploadStates((prev) => ({
			...prev,
			[listType]: {
				isUploading: false,
				fileName: null,
				packageIds: [],
				error: null,
				isUploaded: false,
			},
		}));
	};

	// Helper function to safely get filter options
	const getFilterOption = (key) => {
		if (!filterOptions || !filterOptions[key]) return [];

		// Handle hierarchical category structure
		if (key === "packageCategoryL1" && filterOptions.categoryMap) {
			return Object.keys(filterOptions.categoryMap);
		}

		if (key === "packageCategoryL2" && filterOptions.categoryMap) {
			const selectedL1 = filters.packageCategoryL1 || [];
			if (selectedL1.length === 0) {
				// No L1 selected, show all L2 options
				return [];
			} else {
				// Filter L2 based on selected L1
				const availableL2 = new Set();
				selectedL1.forEach((l1Category) => {
					const l2Items = filterOptions.categoryMap[l1Category];
					// Check if l2Items exists and is an array before iterating
					if (l2Items && Array.isArray(l2Items)) {
						l2Items.forEach((item) => {
							if (item !== "null" && item !== "Null" && item !== null) {
								availableL2.add(item);
							}
						});
					}
				});
				return Array.from(availableL2);
			}
		}

		return Array.isArray(filterOptions[key]) ? filterOptions[key] : [];
	};

	// Clear L2 selections when L1 changes
	useEffect(() => {
		// Don't auto-clear L2 during LLM load - let the filters settle first
		if (filters.packageCategoryL1?.length === 0) {
			// Only clear L2 if user manually cleared all L1
			if (
				!filters.packageCategoryL2 ||
				filters.packageCategoryL2.length === 0
			) {
				return; // Already cleared, nothing to do
			}
			handleUpdateFilter("packageCategoryL2", []);
		} else if (
			filters.packageCategoryL1?.length > 0 &&
			filters.packageCategoryL2?.length > 0
		) {
			// Filter out L2 selections that are no longer valid
			const validL2Options = getFilterOption("packageCategoryL2");

			// If validL2Options is empty, it means we're still loading - don't filter yet
			if (validL2Options.length === 0) {
				console.log("⏳ L2 options not ready yet, skipping validation");
				return;
			}

			const validL2Selections = filters.packageCategoryL2.filter((l2) =>
				validL2Options.includes(l2),
			);

			if (validL2Selections.length !== filters.packageCategoryL2.length) {
				console.log("🔧 Filtering invalid L2 selections:", {
					before: filters.packageCategoryL2,
					after: validL2Selections,
				});
				handleUpdateFilter("packageCategoryL2", validL2Selections);
			}
		}
	}, [filters.packageCategoryL1]);

	if (!showFilters) return null;

	return (
		<div className="filters-sidebar">
			<div className="filters-header">
				<h2 className="filters-title" style={{ fontSize: "16px" }}>
					Package Filters
				</h2>
				<div className="filters-actions">
					<button
						className="btn-medium-primary"
						onClick={onClearAllFilters}
						style={{ fontSize: "12px" }}
					>
						Clear All
					</button>
					<button onClick={onClose} className="close-btn">
						<BurgerIcon />
					</button>
				</div>
			</div>

			{isLoadingFilters && (
				<div className="loading-message">Loading filter options...</div>
			)}

			{filterError && (
				<div className="error-message">
					Error loading filters: {filterError}
				</div>
			)}

			<div className="filters-content" ref={filtersContentRef}>
				{/* 1. Targeting & Location */}
				<CategoryHeader
					title="Targeting Filters"
					isExpanded={expandedCategories.targeting}
					onClick={() => toggleCategory("targeting")}
				>
					{/* Country */}
					<div className="filter-section">
						<SectionHeader
							title="Country"
							isExpanded={expandedSections.country}
							onToggle={() => onToggleSection("country")}
							hasActiveFilters={filters.country?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.country && (
							<div className="section-content">
								<CountryMultiSelectDropdown
									label=""
									options={getFilterOption("country")}
									value={filters.country || []}
									onChange={(selected) =>
										handleUpdateFilter("country", selected)
									}
									placeholder="Select countries..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Ad Placement */}
					<div className="filter-section">
						<SectionHeader
							title="Ad Placement"
							isExpanded={expandedSections.adPlacement}
							onToggle={() => onToggleSection("adPlacement")}
							hasActiveFilters={filters.adPlacement?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.adPlacement && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("adPlacement")}
									value={filters.adPlacement || []}
									onChange={(selected) =>
										handleUpdateFilter("adPlacement", selected)
									}
									placeholder="Select ad placements..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Device Types */}
					<div className="filter-section">
						<SectionHeader
							title="Device Types"
							isExpanded={expandedSections.deviceTypes}
							onToggle={() => onToggleSection("deviceTypes")}
							hasActiveFilters={filters.deviceTypes?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.deviceTypes && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("deviceTypes")}
									value={filters.deviceTypes || []}
									onChange={(selected) =>
										handleUpdateFilter("deviceTypes", selected)
									}
									placeholder="Select device types..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Distribution Channel */}
					<div className="filter-section">
						<SectionHeader
							title="Distribution Channel"
							isExpanded={expandedSections.distributionChannel}
							onToggle={() => onToggleSection("distributionChannel")}
							hasActiveFilters={filters.distributionChannel?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.distributionChannel && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("distributionChannel")}
									value={filters.distributionChannel || []}
									onChange={(selected) =>
										handleUpdateFilter("distributionChannel", selected)
									}
									placeholder="Select distribution channels..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* URL Targeting Type */}
					<div className="filter-section">
						<SectionHeader
							title="URL Targeting Type"
							isExpanded={expandedSections.urlTargetingType}
							onToggle={() => onToggleSection("urlTargetingType")}
							hasActiveFilters={filters.urlTargetingType?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.urlTargetingType && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("urlTargetingType")}
									value={filters.urlTargetingType || []}
									onChange={(selected) =>
										handleUpdateFilter("urlTargetingType", selected)
									}
									placeholder="Select URL targeting types..."
									showLimit={50}
								/>
							</div>
						)}
					</div>
				</CategoryHeader>

				{/* 2. Package Categories */}
				<CategoryHeader
					title="Package Categories"
					isExpanded={expandedCategories.packageDetails}
					onClick={() => toggleCategory("packageDetails")}
				>
					{/* Package Category L1 */}
					<div className="filter-section">
						<SectionHeader
							title="Category L1"
							isExpanded={expandedSections.packageCategoryL1}
							onToggle={() => onToggleSection("packageCategoryL1")}
							hasActiveFilters={filters.packageCategoryL1?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.packageCategoryL1 && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("packageCategoryL1")}
									value={filters.packageCategoryL1 || []}
									onChange={(selected) =>
										handleUpdateFilter("packageCategoryL1", selected)
									}
									placeholder="Select primary categories..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Package Category L2 */}
					<div className="filter-section">
						<SectionHeader
							title="Category L2"
							isExpanded={expandedSections.packageCategoryL2}
							onToggle={() => onToggleSection("packageCategoryL2")}
							hasActiveFilters={filters.packageCategoryL2?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.packageCategoryL2 && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("packageCategoryL2")}
									value={filters.packageCategoryL2 || []}
									onChange={(selected) =>
										handleUpdateFilter("packageCategoryL2", selected)
									}
									placeholder="Select secondary categories..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Package Category L3 */}
					{/* <div className="filter-section">
                        <SectionHeader
                            title="Category L3"
                            isExpanded={expandedSections.packageCategoryL3}
                            onToggle={() => onToggleSection('packageCategoryL3')}
                            hasActiveFilters={filters.packageCategoryL3?.length > 0}
                            style={{ fontSize: '12px' }}
                        />
                        {expandedSections.packageCategoryL3 && (
                            <div className="section-content">
                                <MultiSelectDropdown
                                    label=""
                                    options={getFilterOption('packageCategoryL3')}
                                    value={filters.packageCategoryL3 || []}
                                    onChange={(selected) => handleUpdateFilter('packageCategoryL3', selected)}
                                    placeholder="Select tertiary categories..."
                                    showLimit={50}
                                />
                            </div>
                        )}
                    </div> */}

					{/* Primary Vertical */}
					<div className="filter-section">
						<SectionHeader
							title="Primary Vertical"
							isExpanded={expandedSections.primaryVertical}
							onToggle={() => onToggleSection("primaryVertical")}
							hasActiveFilters={filters.primaryVertical?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.primaryVertical && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("primaryVertical")}
									value={filters.primaryVertical || []}
									onChange={(selected) =>
										handleUpdateFilter("primaryVertical", selected)
									}
									placeholder="Select verticals..."
									showLimit={50}
								/>
							</div>
						)}
					</div>
				</CategoryHeader>

				{/* 3. Deal Information */}
				<CategoryHeader
					title="Deal Information"
					isExpanded={expandedCategories.dealInfo}
					onClick={() => toggleCategory("dealInfo")}
				>
					{/* Deal Name */}
					<div className="filter-section">
						<SectionHeader
							title="Deal Name"
							isExpanded={expandedSections.dealName}
							onToggle={() => onToggleSection("dealName")}
							hasActiveFilters={filters.dealName?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.dealName && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("dealName")}
									value={filters.dealName || []}
									onChange={(selected) =>
										handleUpdateFilter("dealName", selected)
									}
									placeholder="Select deal names..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Deal ID */}
					<div className="filter-section">
						<SectionHeader
							title="Deal ID"
							isExpanded={expandedSections.dealId}
							onToggle={() => onToggleSection("dealId")}
							hasActiveFilters={filters.dealId?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.dealId && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("dealId")}
									value={filters.dealId || []}
									onChange={(selected) =>
										handleUpdateFilter("dealId", selected)
									}
									placeholder="Select deal IDs..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Deal Source */}
					<div className="filter-section">
						<SectionHeader
							title="Deal Source"
							isExpanded={expandedSections.dealSource}
							onToggle={() => onToggleSection("dealSource")}
							hasActiveFilters={filters.dealSource?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.dealSource && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("dealSource")}
									value={filters.dealSource || []}
									onChange={(selected) =>
										handleUpdateFilter("dealSource", selected)
									}
									placeholder="Select deal sources..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Deal Status */}
					<div className="filter-section">
						<SectionHeader
							title="Deal Status"
							isExpanded={expandedSections.dealStatus}
							onToggle={() => onToggleSection("dealStatus")}
							hasActiveFilters={filters.dealStatus?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.dealStatus && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("dealStatus")}
									value={filters.dealStatus || []}
									onChange={(selected) =>
										handleUpdateFilter("dealStatus", selected)
									}
									placeholder="Select deal statuses..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Account ID */}
					<div className="filter-section">
						<SectionHeader
							title="Account ID"
							isExpanded={expandedSections.accountId}
							onToggle={() => onToggleSection("accountId")}
							hasActiveFilters={filters.accountId?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.accountId && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("accountId")}
									value={filters.accountId || []}
									onChange={(selected) =>
										handleUpdateFilter("accountId", selected)
									}
									placeholder="Select account IDs..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Publisher Account */}
					<div className="filter-section">
						<SectionHeader
							title="Publisher Account"
							isExpanded={expandedSections.publisherAccountNk}
							onToggle={() => onToggleSection("publisherAccountNk")}
							hasActiveFilters={filters.publisherAccountNk?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.publisherAccountNk && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("publisherAccountNk")}
									value={filters.publisherAccountNk || []}
									onChange={(selected) =>
										handleUpdateFilter("publisherAccountNk", selected)
									}
									placeholder="Select publisher accounts..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Associated Advertisers */}
					<div className="filter-section">
						<SectionHeader
							title="Associated Advertisers"
							isExpanded={expandedSections.associatedAdvertisers}
							onToggle={() => onToggleSection("associatedAdvertisers")}
							hasActiveFilters={filters.associatedAdvertisers?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.associatedAdvertisers && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("associatedAdvertisers")}
									value={filters.associatedAdvertisers || []}
									onChange={(selected) =>
										handleUpdateFilter("associatedAdvertisers", selected)
									}
									placeholder="Select advertisers..."
									showLimit={50}
								/>
							</div>
						)}
					</div>
				</CategoryHeader>

				{/* 4. Audience Information */}
				<CategoryHeader
					title="Audience Information"
					isExpanded={expandedCategories.audienceInfo}
					onClick={() => toggleCategory("audienceInfo")}
				>
					{/* Audience ID */}
					<div className="filter-section">
						<SectionHeader
							title="Audience ID"
							isExpanded={expandedSections.audienceId}
							onToggle={() => onToggleSection("audienceId")}
							hasActiveFilters={filters.audienceId?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.audienceId && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("audienceId")}
									value={filters.audienceId || []}
									onChange={(selected) =>
										handleUpdateFilter("audienceId", selected)
									}
									placeholder="Select audience IDs..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Audience Name */}
					<div className="filter-section">
						<SectionHeader
							title="Audience Name"
							isExpanded={expandedSections.audienceName}
							onToggle={() => onToggleSection("audienceName")}
							hasActiveFilters={filters.audienceName?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.audienceName && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("audienceName")}
									value={filters.audienceName || []}
									onChange={(selected) =>
										handleUpdateFilter("audienceName", selected)
									}
									placeholder="Select audience names..."
									showLimit={50}
								/>
							</div>
						)}
					</div>

					{/* Audience Provider Name */}
					<div className="filter-section">
						<SectionHeader
							title="Audience Provider"
							isExpanded={expandedSections.audienceProviderName}
							onToggle={() => onToggleSection("audienceProviderName")}
							hasActiveFilters={filters.audienceProviderName?.length > 0}
							style={{ fontSize: "12px" }}
						/>
						{expandedSections.audienceProviderName && (
							<div className="section-content">
								<MultiSelectDropdown
									label=""
									options={getFilterOption("audienceProviderName")}
									value={filters.audienceProviderName || []}
									onChange={(selected) =>
										handleUpdateFilter("audienceProviderName", selected)
									}
									placeholder="Select providers..."
									showLimit={50}
								/>
							</div>
						)}
					</div>
				</CategoryHeader>

				<CategoryHeader
					title="Scale and Performance"
					isExpanded={expandedCategories.scale}
					onClick={() => toggleCategory("scale")}
				>
					<div className="filter-section">
						<ThresholdInput
							field="impressions"
							label="Impressions"
							hint="0"
							placeholder="0"
							value={filters.impressions}
							defaultValue="0"
							onChange={(field, value) =>
								handleUpdateFilter("impressions", value)
							}
						/>

						<ThresholdInput
							field="totalSpendInUSD"
							label="Spend"
							hint="0"
							placeholder="0"
							value={filters.totalSpendInUSD}
							defaultValue="0"
							onChange={(field, value) =>
								handleUpdateFilter("totalSpendInUSD", value)
							}
						/>

						<ThresholdInput
							field="bidCpm"
							label="Bid CPM"
							hint="0"
							placeholder="0"
							value={filters.bidCpm}
							defaultValue="0"
							onChange={(field, value) => handleUpdateFilter("bidCpm", value)}
						/>

						<ThresholdInput
							field="viewability"
							label="Viewability"
							hint="0"
							placeholder="0"
							value={filters.viewability}
							defaultValue="0"
							onChange={(field, value) =>
								handleUpdateFilter("viewability", value)
							}
						/>
					</div>
				</CategoryHeader>

				{/* Result Limit and Upload Section */}
				<div
					style={{
						marginTop: "16px",
						padding: "12px",
						borderTop: "1px solid #e5e7eb",
						backgroundColor: "#f9fafb",
					}}
				>
					{/* Result Limit */}
					<div style={{ marginBottom: "12px" }}>
						<label
							style={{
								display: "block",
								fontSize: "11px",
								fontWeight: "500",
								color: "#374151",
								marginBottom: "4px",
							}}
						>
							Result Limit:
						</label>
						<input
							type="number"
							placeholder="1000"
							min="1"
							value={filters.resultLimit || ""}
							onChange={(e) =>
								handleUpdateFilter("resultLimit", parseInt(e.target.value))
							}
							style={{
								width: "100%",
								padding: "6px 8px",
								border: "1px solid #d1d5db",
								borderRadius: "4px",
								fontSize: "11px",
								backgroundColor: "white",
								color: "#374151",
								boxSizing: "border-box",
							}}
						/>
					</div>

					{/* Upload Section */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: "4px",
							margin: "0 0 8px 0",
							position: "relative",
						}}
					>
						<p
							style={{
								margin: "0",
								fontSize: "11px",
								color: "#6b7280",
								textAlign: "center",
							}}
						>
							Upload CSV files for package filtering
						</p>
						<div
							style={{ position: "relative", display: "inline-flex" }}
							onMouseEnter={() => setShowUploadTooltip(true)}
							onMouseLeave={() => setShowUploadTooltip(false)}
						>
							<div
								style={{
									display: "inline-flex",
									alignItems: "center",
									justifyContent: "center",
									width: "14px",
									height: "14px",
									borderRadius: "50%",
									backgroundColor: "#6b7280",
									color: "white",
									fontSize: "9px",
									fontWeight: "bold",
									cursor: "pointer",
								}}
							>
								i
							</div>

							{showUploadTooltip && (
								<div
									style={{
										position: "absolute",
										top: "-45px",
										left: "-50px",
										transform: "translateX(-50%)",
										background: "#333",
										color: "white",
										padding: "6px 8px",
										borderRadius: "4px",
										fontSize: "10px",
										width: "120px",
										textAlign: "center",
										lineHeight: "1.3",
										zIndex: 1000,
									}}
								>
									CSV file must contain a package name/ID column
								</div>
							)}
						</div>
					</div>

					<div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
						{/* Include List Button */}
						<div style={{ flex: 1 }}>
							<input
								type="file"
								accept=".csv"
								onChange={(e) =>
									handleFileUpload(e.target.files[0], "includeList")
								}
								style={{ display: "none" }}
								id="include-upload"
								disabled={uploadStates.includeList.isUploading}
							/>
							<label
								htmlFor="include-upload"
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "4px",
									width: "100%",
									padding: "6px 8px",
									background: uploadStates.includeList.isUploading
										? "#6b7280"
										: "#0076BD",
									color: "white",
									border: "none",
									borderRadius: "4px",
									fontSize: "10px",
									fontWeight: "500",
									cursor: uploadStates.includeList.isUploading
										? "not-allowed"
										: "pointer",
									transition: "all 0.2s",
									textDecoration: "none",
									boxSizing: "border-box",
								}}
							>
								{uploadStates.includeList.isUploading ? (
									<>⏳ Uploading...</>
								) : (
									<>
										<UploadIcon />
										Allow List
									</>
								)}
							</label>
						</div>

						{/* Exclude List Button */}
						<div style={{ flex: 1 }}>
							<input
								type="file"
								accept=".csv"
								onChange={(e) =>
									handleFileUpload(e.target.files[0], "blockedList")
								}
								style={{ display: "none" }}
								id="exclude-upload"
								disabled={uploadStates.blockedList.isUploading}
							/>
							<label
								htmlFor="exclude-upload"
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "4px",
									width: "100%",
									padding: "6px 8px",
									background: uploadStates.blockedList.isUploading
										? "#6b7280"
										: "#dc2626",
									color: "white",
									border: "none",
									borderRadius: "4px",
									fontSize: "10px",
									fontWeight: "500",
									cursor: uploadStates.blockedList.isUploading
										? "not-allowed"
										: "pointer",
									transition: "all 0.2s",
									textDecoration: "none",
									boxSizing: "border-box",
								}}
							>
								{uploadStates.blockedList.isUploading ? (
									<>⏳ Uploading...</>
								) : (
									<>
										<UploadIcon />
										Block List
									</>
								)}
							</label>
						</div>
					</div>

					{/* Display uploaded file status */}
					{uploadStates.includeList.isUploaded && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								padding: "4px 6px",
								backgroundColor: "#dcfce7",
								border: "1px solid #22c55e",
								borderRadius: "3px",
								fontSize: "9px",
								marginBottom: "4px",
							}}
						>
							<span style={{ color: "#15803d", fontWeight: "500" }}>
								Allow List: {uploadStates.includeList.fileName}
							</span>
							<button
								onClick={() => clearUploadedList("includeList")}
								style={{
									background: "none",
									border: "none",
									color: "#dc2626",
									cursor: "pointer",
									padding: "2px",
									borderRadius: "2px",
									display: "flex",
									alignItems: "center",
									fontSize: "12px",
								}}
								title="Remove Allow List"
							>
								<RemoveIcon />
							</button>
						</div>
					)}

					{uploadStates.blockedList.isUploaded && (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								padding: "4px 6px",
								backgroundColor: "#fee2e2",
								border: "1px solid #dc2626",
								borderRadius: "3px",
								fontSize: "9px",
								marginBottom: "4px",
							}}
						>
							<span style={{ color: "#dc2626", fontWeight: "500" }}>
								Block List: {uploadStates.blockedList.fileName}
							</span>
							<button
								onClick={() => clearUploadedList("blockedList")}
								style={{
									background: "none",
									border: "none",
									color: "#dc2626",
									cursor: "pointer",
									padding: "2px",
									borderRadius: "2px",
									display: "flex",
									alignItems: "center",
									fontSize: "12px",
								}}
								title="Remove Block List"
							>
								<RemoveIcon />
							</button>
						</div>
					)}
				</div>
			</div>

			<style jsx>{`
                .loading-message, .error-message {
                    padding: 16px;
                    text-align: center;
                    font-size: 12px;
                }

                .loading-message {
                    color: #6b7280;
                }

                .error-message {
                    color: #dc2626;
                    background: #fef2f2;
                    border: 1px solid #fca5a5;
                    border-radius: 4px;
                    margin: 8px;
                }

                .btn-medium-primary {
                    padding: 6px 12px;
                    background: #0076BD;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .btn-medium-primary:hover {
                    background: #005a94;
                }

                .close-btn {
                    background: none;
                    border: none;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 4px;
                    border-radius: 4px;
                    transition: all 0.2s;
                }

                .close-btn:hover {
                    background: #f3f4f6;
                    color: #374151;
                }

                /* Threshold Filter Styles */
.threshold-filter-container {
    margin-bottom: 16px;
}

.threshold-filter-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
}

.threshold-filter-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.threshold-input-group {
    display: flex;
    flex-direction: column;
}

.threshold-input-label {
    font-size: 10px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
}

.threshold-hint {
    font-size: 9px;
    color: #6b7280;
    font-weight: 400;
    background: #f3f4f6;
    padding: 1px 4px;
    border-radius: 3px;
}

.threshold-input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.threshold-input {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 11px;
    background: white;
    transition: border-color 0.2s ease;
}

.threshold-input:focus {
    outline: none;
    border-color: #0076BD;
    box-shadow: 0 0 0 1px #0076BD;
}

.threshold-display {
    font-size: 9px;
    color: #0076BD;
    font-weight: 500;
    background: #eff6ff;
    padding: 2px 6px;
    border-radius: 3px;
    border: 1px solid #bfdbfe;
}

.threshold-help {
    margin-top: 8px;
    padding: 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
}

.threshold-help small {
    font-size: 10px;
    color: #6b7280;
    line-height: 1.3;
}

                /* MultiSelectDropdown Styles */
                .multiselect-container {
                    position: relative;
                    width: 100%;
                }

                .multiselect-label {
                    display: block;
                    font-size: 12px;
                    font-weight: 500;
                    color: #374151;
                    margin-bottom: 4px;
                }

                .selected-count {
                    color: #6b7280;
                    font-weight: 400;
                    margin-left: 4px;
                }

                .multiselect-trigger {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    background: white;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 12px;
                }

                .multiselect-trigger:hover {
                    border-color: #0076BD;
                }

                .multiselect-trigger.open {
                    border-color: #0076BD;
                    box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
                }

                .multiselect-placeholder {
                    color: #6b7280;
                    font-size: 12px;
                    flex: 1;
                }

                .multiselect-dropdown-portal {
                    background: white;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .multiselect-header {
                    padding: 12px;
                    border-bottom: 1px solid #e5e7eb;
                    background: #f9fafb;
                }

                .multiselect-search {
                    width: 100%;
                    padding: 6px 8px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    font-size: 12px;
                    margin-bottom: 8px;
                    box-sizing: border-box;
                }

                .multiselect-search:focus {
                    outline: none;
                    border-color: #0076BD;
                    box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
                }

                .multiselect-actions {
                    display: flex;
                    gap: 8px;
                }

                .select-all-btn, .clear-all-btn {
                    flex: 1;
                    padding: 6px 8px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    background: white;
                    color: #374151;
                    font-size: 11px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .select-all-btn:hover, .clear-all-btn:hover {
                    background: #f3f4f6;
                }

                .multiselect-options {
                    overflow-y: auto;
                    max-height: 300px;
                }

                .multiselect-option {
                    display: flex;
                    align-items: center;
                    padding: 8px 12px;
                    cursor: pointer;
                    transition: background-color 0.2s;
                    font-size: 12px;
                    gap: 8px;
                }

                .multiselect-option:hover {
                    background: #f9fafb;
                }

                .multiselect-option.selected {
                    background: #eff6ff;
                    color: #0076BD;
                }

                .multiselect-option input[type="checkbox"] {
                    margin: 0;
                    cursor: pointer;
                }

                .option-text {
                    flex: 1;
                    user-select: none;
                }

                .multiselect-show-controls {
                    padding: 8px 12px;
                    border-top: 1px solid #e5e7eb;
                    background: #f9fafb;
                    text-align: center;
                }

                .show-more-btn, .show-less-btn {
                    padding: 6px 12px;
                    background: none;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    color: #0076BD;
                    font-size: 11px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .show-more-btn:hover, .show-less-btn:hover {
                    background: #f3f4f6;
                    border-color: #0076BD;
                }

                .no-options {
                    padding: 20px;
                    text-align: center;
                    color: #6b7280;
                    font-size: 12px;
                    font-style: italic;
                }

                .multiselect-footer {
                    padding: 8px 12px;
                    border-top: 1px solid #e5e7eb;
                    background: #f8f9fa;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .multiselect-help-text {
                    font-size: 9px;
                    color: #6b7280;
                    font-style: italic;
                }

                .done-button {
                    background: #0076BD;
                    color: white;
                    border: none;
                    border-radius: 4px;
                    padding: 6px 12px;
                    font-size: 11px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: background-color 0.2s;
                }

                .done-button:hover {
                    background: #005a94;
                }

                /* Country-specific styling */
                .region-header {
                    padding: 8px 12px;
                    background: #f3f4f6;
                    border-bottom: 1px solid #e5e7eb;
                    font-size: 11px;
                    font-weight: 600;
                    color: #374151;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .region-option {
                    padding-left: 24px;
                    background: #fafbfc;
                }

                .region-option:hover {
                    background: #f0f9ff;
                }

                .region-option.selected {
                    background: #dbeafe;
                }
            `}</style>
		</div>
	);
};
